import { dataByCompanyBusinessStatus } from "@/constants/terminal/company-business-status"
import { dataByCompanyProfileType } from "@/constants/terminal/company-profile-type"
import { dataByCompanyService } from "@/constants/terminal/company-service"
import { dataByCompanyTimelineEventKind } from "@/constants/terminal/company-timeline-event-kind"
import { dataByCompanyTimelineSourceKind } from "@/constants/terminal/company-timeline-source-kind"
import { SearchSubjectKind } from "@/constants/terminal/search-subject-kind"
import {
  formatTerminalDate,
  formatTerminalExportDate,
} from "@/lib/terminal/date"
import type {
  TerminalComparisonView,
  TerminalFileView,
} from "@/lib/terminal/file-view"
import type {
  TerminalBenchmarkFrame,
  TerminalBenchmarkMetricKey,
  TerminalTradeKey,
} from "@/lib/terminal/types"
import type { CompanyProfile } from "@/terminal/file/company-profile-value-schemas"

const isEntityTimelineShown = false

// The Company File page's display models, built from the buyer's
// TerminalFileView alone. The live comparison views become the metric boxes,
// and the Company Profile and File Scope's names become the Background boxes.

export type TerminalBenchmarkRowModel = {
  label: "vs portfolio"
  // Median of the portfolio — "the typical portfolio company", deliberately not
  // the mean (review volume is power-law distributed; see TerminalBenchmarkStat).
  value: string
  delta: string
  // The only colour in File data: better or worse than the portfolio, by the
  // metric's polarity; the verdict stays in the Detections and the Brief
  // (owner ruling D56, 2026-10-24).
  tone: "good" | "bad" | "neutral"
  // Companies with a computable value for this metric, not review count.
  n: number
}

export type TerminalMetricRowModel = {
  key: string
  label: string
  value: string
  benchmarks: TerminalBenchmarkRowModel[]
  // "star" appends the ★ rating glyph after the rating value.
  unit?: "star"
}

type TerminalTradeRowModel = {
  key: TerminalTradeKey
  label: string
  share: string
  satisfaction: string
}

type TerminalKVRowModel = {
  key: string
  label: string
  value: string
  href?: string
}

export type TerminalLicenseRowModel = {
  key: string
  state: string
  licenseType: string
  licenseNumber: string
  // Authority, stated status and expiry, for the export; the page shows the
  // state, type and number only.
  detail: string
}

type TerminalTimelineEventModel = {
  key: string
  date: string
  label: string
  notes: string
  sourceLabel: string
}

export type TerminalPlatformRowModel = {
  key: string
  name: string
  officialName: string
  url: string
  rating: number | null
  reviewCount: number | null
}

export type TerminalFileSectionModel =
  | {
      kind: "metrics"
      id: string
      title: string
      rows: TerminalMetricRowModel[]
      // Reviews' review profiles, folded under its metrics.
      platforms?: TerminalPlatformRowModel[]
    }
  | {
      kind: "trade"
      id: string
      title: string
      rows: TerminalTradeRowModel[]
      // The Company Profile's services, as the Company markets them.
      servicesMarketed: string[]
    }
  | {
      kind: "kv"
      id: string
      title: string
      rows: TerminalKVRowModel[]
      // Background closes on the Company's licences.
      licensedIn: string[]
      licenseRows: TerminalLicenseRowModel[]
    }
  | {
      kind: "timeline"
      id: string
      title: string
      rows: TerminalTimelineEventModel[]
    }

// An event's own day, or "Undated" when no record states one.
const formatEventDay = (day: string | null) =>
  day ? formatTerminalDate(day) : "Undated"

// Every File box in reading order: Reviews (the rating, the review counts and
// the review platforms) first, the other metric boxes, then Background with
// its licences.
export const buildFileSections = (
  file: Pick<
    TerminalFileView,
    "company" | "profile" | "fileScope" | "comparison"
  >
): TerminalFileSectionModel[] => {
  // A profile can be captured by more than one Source Record; list each
  // profile (by URL) once, with its fullest capture. Only profiles with
  // reviews are listed, as before the new pipeline (its platform listing kept
  // a platform only when it had reviews).
  const platforms = (file.profile?.platforms ?? [])
    .filter(({ reviewCount }) => (reviewCount ?? 0) > 0)
    .toSorted((a, b) => (b.reviewCount ?? 0) - (a.reviewCount ?? 0))
    .filter(
      (platform, index, all) =>
        all.findIndex(({ url }) => url === platform.url) === index
    )
    .map((platform) => ({
      key: platform.sourceRecordId,
      name: platform.platformName,
      officialName: platform.officialName,
      url: platform.url,
      rating: platform.rating,
      reviewCount: platform.reviewCount,
    }))
  const servicesMarketed = (file.profile?.services?.value ?? [])
    .map((service) => dataByCompanyService[service].label)
    .sort()
  const comparisonSections: TerminalFileSectionModel[] = file.comparison
    ? buildComparisonSections(file.comparison, servicesMarketed)
    : []
  // Without review figures, Trade Mix still states the services marketed.
  if (!file.comparison && servicesMarketed.length > 0)
    comparisonSections.push({
      kind: "trade",
      id: "trade-profile",
      title: "Trade Mix",
      rows: [],
      servicesMarketed,
    })
  const reviews = comparisonSections.find((section) => section.id === "reviews")
  if (platforms.length > 0) {
    if (reviews?.kind === "metrics") reviews.platforms = platforms
    else
      comparisonSections.unshift({
        kind: "metrics",
        id: "reviews",
        title: "Reviews",
        rows: [],
        platforms,
      })
  }
  return [...comparisonSections, ...buildBackgroundSections(file)]
}

const buildBackgroundSections = ({
  company,
  profile,
  fileScope,
}: Pick<
  TerminalFileView,
  "company" | "profile" | "fileScope"
>): TerminalFileSectionModel[] => {
  const sections: TerminalFileSectionModel[] = []
  const companyRows = backgroundRows(company.name, profile, fileScope)
  const licenseRows = (profile?.licences ?? []).map((licence) => ({
    key: licence.sourceRecordId,
    state: stateCode(licence.subdivisionCode),
    licenseType: licence.licenceType ?? "Contractor",
    licenseNumber: `#${licence.number}`,
    detail: [
      licence.authorityName,
      licence.status,
      // Only the export writes a licence's detail, in its own date form.
      licence.expiresOn &&
        `expires ${formatTerminalExportDate(licence.expiresOn)}`,
    ]
      .filter(Boolean)
      .join(" · "),
  }))
  if (companyRows.length > 0 || licenseRows.length > 0)
    sections.push({
      kind: "kv",
      id: "background",
      title: "Background",
      rows: companyRows,
      licensedIn: [...new Set(licenseRows.map((row) => row.state))].sort(),
      licenseRows,
    })
  if (!profile) return sections
  // The Entity Timeline is held back from the Night Shift release (Dhanur,
  // 24 Oct 2026); it returns when it is designed on its own.
  if (isEntityTimelineShown && profile.timeline.length > 0)
    sections.push({
      kind: "timeline",
      id: "entity-timeline",
      title: "Entity Timeline",
      rows: profile.timeline
        // Newest first; an undated event sorts last.
        .toSorted(
          (a, b) =>
            Number(a.date === null) - Number(b.date === null) ||
            (b.date ?? "").localeCompare(a.date ?? "")
        )
        .map((event, index) => ({
          key: `${event.kind}-${event.date ?? "undated"}-${index}`,
          date: formatEventDay(event.date),
          label: dataByCompanyTimelineEventKind[event.kind].label,
          notes: event.label,
          sourceLabel: dataByCompanyTimelineSourceKind[event.sourceKind].label,
        })),
    })
  return sections
}

const normalizeName = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "")

// Production's row order. Legal Name and Also Known As are the current legal
// entities and brands in File Scope whose names differ from the Company's.
const backgroundRows = (
  companyName: string,
  profile: CompanyProfile | null,
  fileScope: TerminalFileView["fileScope"]
): TerminalKVRowModel[] => {
  const rows: TerminalKVRowModel[] = []
  const push = (key: string, label: string, value: string, href?: string) =>
    rows.push({ key, label, value, href })
  const seenNames = new Set([normalizeName(companyName)])
  const scopeNames = (kind: SearchSubjectKind) =>
    fileScope.scopeEntries
      .filter((entry) => entry.kind === kind && entry.effectiveTo === null)
      .map(({ name }) => name)
      .filter((name) => {
        const normalized = normalizeName(name)
        if (!normalized || seenNames.has(normalized)) return false
        seenNames.add(normalized)
        return true
      })
  // Only the first row of a multi-row value carries the label.
  scopeNames(SearchSubjectKind.LEGAL_ENTITY).forEach((name, index) =>
    push(`legal-name-${index}`, index === 0 ? "Legal Name" : "", name)
  )
  scopeNames(SearchSubjectKind.BRAND).forEach((name, index) =>
    push(`alias-${index}`, index === 0 ? "Also Known As" : "", name)
  )
  if (!profile) return rows
  if (profile.website)
    push(
      "website",
      "Website",
      new URL(profile.website).hostname.replace(/^www\./i, ""),
      profile.website
    )
  // The year alone: founders and owners are People, held back (D57).
  if (profile.yearEstablished)
    push("founded", "Founded", String(profile.yearEstablished.value))
  if (profile.companyType)
    push(
      "type",
      "Type",
      dataByCompanyProfileType[profile.companyType.value].label
    )
  if (profile.companyStatus)
    push(
      "status",
      "Status",
      dataByCompanyBusinessStatus[profile.companyStatus.value].label
    )
  if (profile.isFranchise)
    push("franchise", "Franchise", profile.isFranchise.value ? "Yes" : "No")
  // Key People is held back with People (D57): the company page and the
  // export leave it out until People is designed (Dhanur, 25 Oct 2026).
  if (profile.headquarters)
    push(
      "hq",
      "HQ",
      `${profile.headquarters.value.city}, ${stateCode(profile.headquarters.value.subdivisionCode)}`
    )
  if (profile.serviceAreaStates.length > 0)
    push(
      "service-area",
      "Service Area",
      profile.serviceAreaStates.map(stateCode).sort().join(", ")
    )
  return rows
}

// "US-AZ" → "AZ".
const stateCode = (subdivisionCode: string) =>
  subdivisionCode.replace(/^US-/, "")

type MetricFormatKind = "rating" | "percent" | "count"

const NO_DATA = "Insufficient Data"

const TRADE_LABELS: Record<TerminalTradeKey, string> = {
  roofing: "Roofing",
  storage: "Storage",
  electrical: "Electrical",
  hvac: "HVAC",
}

const WORK_QUALITY_ROWS: ReadonlyArray<{
  metricKey: TerminalBenchmarkMetricKey
  label: string
}> = [
  { metricKey: "salesConduct", label: "Sales Conduct" },
  { metricKey: "workmanship", label: "Workmanship" },
  { metricKey: "value", label: "Value" },
  { metricKey: "postSaleSupport", label: "Customer Support" },
  { metricKey: "projectManagement", label: "Project Management" },
]

// The benchmark metrics, the trade mix and the activity quarters. Review
// manipulation shows only as its Detection, and platform spread is dropped
// (owner decision 7). Verified-buyer counts and their trend are commercial
// data and never reach a buyer (owner ruling, 2026-10-14).
const buildComparisonSections = (
  comparison: Omit<TerminalComparisonView, "asOf">,
  servicesMarketed: string[]
): TerminalFileSectionModel[] => {
  const { metrics, portfolioFrame } = comparison
  const row = (
    metricKey: TerminalBenchmarkMetricKey,
    label: string,
    format: MetricFormatKind,
    options: Omit<
      MetricRowInput,
      "metricKey" | "label" | "format" | "value" | "portfolioFrame"
    > = {}
  ) =>
    metricRow({
      metricKey,
      label,
      format,
      value: metrics[metricKey] ?? null,
      portfolioFrame,
      ...options,
    })
  const unbenchmarked = { shouldShowBenchmarks: false }

  return [
    {
      // The rating against the portfolio leads; the review counts follow,
      // unbenchmarked.
      kind: "metrics",
      id: "reviews",
      title: "Reviews",
      rows: [
        row("aggregateRating", "Aggregate Rating", "rating", { unit: "star" }),
        row("reviewVolume", "Total Reviews", "count", unbenchmarked),
        row("reviewVelocity", "12-Mo Review Avg", "count", {
          ...unbenchmarked,
          decimals: 1,
        }),
        row("activityTrend", "vs Prior 12-Mo", "percent", {
          ...unbenchmarked,
          isSigned: true,
        }),
      ],
    },
    {
      kind: "metrics",
      id: "character",
      title: "Character",
      rows: [
        row("buyerReviewRate", "Buyer Rate", "percent"),
        row("buyerSatisfaction", "Buyer Satisfaction", "percent"),
        row("ltReviewRate", "Long-Term Review Rate", "percent"),
        row("ltSatisfaction", "Long-Term Satisfaction", "percent"),
        row("deceptionRate", "Deception Rate", "percent", {
          polarity: "lower",
        }),
      ],
    },
    {
      kind: "metrics",
      id: "execution",
      title: "Execution",
      rows: WORK_QUALITY_ROWS.map(({ metricKey, label }) =>
        row(metricKey, label, "percent")
      ),
    },
    {
      // The Company's own scope mix across its reviews (owner ruling R2).
      kind: "trade",
      id: "trade-profile",
      title: "Trade Mix",
      rows: (Object.keys(TRADE_LABELS) as TerminalTradeKey[]).map((key) => {
        const { share, satisfaction } = comparison.trade[key]
        const hasShare = share != null && share !== 0
        return {
          key,
          label: TRADE_LABELS[key],
          share: hasShare ? `${formatNumber(share, 1)}% share` : "--",
          satisfaction:
            hasShare && satisfaction != null
              ? `${formatNumber(satisfaction, 1)}% sat`
              : "--",
        }
      }),
      servicesMarketed,
    },
  ]
}

type MetricRowInput = {
  label: string
  value: number | null
  metricKey: TerminalBenchmarkMetricKey
  format: MetricFormatKind
  polarity?: "higher" | "lower"
  isSigned?: boolean
  decimals?: number
  unit?: "star"
  portfolioFrame: TerminalBenchmarkFrame | null
  shouldShowBenchmarks?: boolean
}

const metricRow = ({
  label,
  value,
  metricKey,
  format,
  polarity = "higher",
  isSigned = false,
  decimals,
  unit,
  portfolioFrame,
  shouldShowBenchmarks = true,
}: MetricRowInput): TerminalMetricRowModel => ({
  key: metricKey,
  label,
  value:
    value == null
      ? NO_DATA
      : formatMetricValue(value, format, isSigned, decimals),
  unit,
  benchmarks:
    shouldShowBenchmarks && value != null
      ? benchmarkRows({
          stat: portfolioFrame?.values[metricKey],
          value,
          format,
          polarity,
          isSigned,
        })
      : [],
})

// A delta colours at 2% of its metric's scale or more: 0.1 on the 0-5 star
// rating, 2 percentage points on a percent. Counts never colour.
const COLOUR_THRESHOLD_BY_FORMAT: Record<MetricFormatKind, number> = {
  rating: 0.1,
  percent: 2,
  count: Infinity,
}

const benchmarkRows = ({
  stat,
  value,
  format,
  polarity,
  isSigned,
}: {
  stat: TerminalBenchmarkFrame["values"][TerminalBenchmarkMetricKey]
  value: number
  format: MetricFormatKind
  polarity: "higher" | "lower"
  isSigned: boolean
}): TerminalBenchmarkRowModel[] => {
  if (stat == null) return []
  const delta = value - stat.median
  const isBetter = polarity === "higher" ? delta > 0 : delta < 0
  let tone: TerminalBenchmarkRowModel["tone"] = "neutral"
  // Rounded first, so a delta shown as "+0.1" never reads neutral.
  if (Math.abs(Number(delta.toFixed(1))) >= COLOUR_THRESHOLD_BY_FORMAT[format])
    tone = isBetter ? "good" : "bad"
  return [
    {
      label: "vs portfolio",
      value: formatMetricValue(stat.median, format, isSigned),
      delta: formatDelta(delta, format),
      tone,
      n: stat.n,
    },
  ]
}

const formatMetricValue = (
  value: number,
  format: MetricFormatKind,
  isSigned = false,
  decimals?: number
): string => {
  switch (format) {
    case "rating":
      // The ★ glyph is the unit (rendered by MetricRow); no "stars" word.
      return value.toFixed(1)
    case "percent": {
      const sign = isSigned && value > 0 ? "+" : ""
      return `${sign}${formatNumber(value, 1)}%`
    }
    case "count":
      if (decimals != null)
        return Number(value.toFixed(decimals)).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      return Number.isInteger(value)
        ? value.toLocaleString()
        : formatNumber(value, 1)
  }
}

// Explicit "+" on positives and a true minus glyph (U+2212) on negatives.
const formatDelta = (value: number, format: MetricFormatKind) => {
  let sign = ""
  if (value > 0) sign = "+"
  if (value < 0) sign = "−"
  const magnitude = formatNumber(Math.abs(value), 1)
  return format === "percent" ? `${sign}${magnitude}pp` : `${sign}${magnitude}`
}

const formatNumber = (value: number, decimals: number): string =>
  Number(value.toFixed(decimals)).toLocaleString(undefined, {
    minimumFractionDigits: Number.isInteger(value) ? 0 : decimals,
    maximumFractionDigits: decimals,
  })
